import { useEffect, useMemo, useState } from 'react'
import { TakeProfitEditCSS } from './TakeProfitEditCSS.styled'
import { onlyDigits } from 'utils/uiUtils_TS'
import { LONG_DASH } from 'constants'
import { currencyFormatter } from 'utils'
import { ASSETS } from 'constants/assets'
import { getPnL } from 'utils/pnl'
import { usePriceFeed } from 'hooks/usePriceFeed'

const INITIAL_VALUE = {
  newTakeProfitPrice: '',
  newTakeProfitValue: '',
  cancelTakeProfitOrder: false,
}

const getAssetInfo = (asset)=>ASSETS.filter((item) => {
  return item.id === asset
})[0]

export const TakeProfitEdit = ({ order = {}, onChange }) => {
  const { takeProfitPrice, takeProfitValue, side, asset, amount, leverage, entryPrice } = order
  
  const { priceFeed } = usePriceFeed(asset)

  const [values, setValues] = useState(INITIAL_VALUE)
  const [isFocused, setFocusInput] = useState({})

  const onInputChange = (e) => {
    const inputName = e.target.name
    const inputValue = onlyDigits(e.target.value)
    setValues((prev) => {
      const newValue = { ...prev, [inputName]: inputValue }
      onChange && onChange(newValue)
      return newValue
    })
  }

  const resetValue = (propName)=> setValues((prev) => {
    const newValue = { ...prev, [propName]: '' }
    onChange && onChange(newValue)
    return newValue
  })

  const onInputClick = (e) => {
    const inputName = e.target.name
    if (inputName === 'newTakeProfitPrice') {
      resetValue('newTakeProfitValue')
      setFocusInput(prev => ({...prev, newTakeProfitPrice: true}))
    }
    if (inputName === 'newTakeProfitValue') {
      resetValue('newTakeProfitPrice')
      setFocusInput(prev => ({...prev, newTakeProfitValue: true}))
    }
  }
  
  const onInputBlur = (e) => {
    const inputName = e.target.name
    const inputValue = onlyDigits(e.target.value)
    if (inputName === 'newTakeProfitPrice' && (!inputValue||!values?.newTakeProfitPrice)) 
      setFocusInput(prev => ({...prev, newTakeProfitPrice: false}))
    if (inputName === 'newTakeProfitValue' && (!inputValue || !values?.newTakeProfitValue)) 
      setFocusInput(prev => ({...prev, newTakeProfitValue: false}))
  }

  const onCheckChange = (e) => {
    const inputName = e.target.name
    const inputCheck = e.target.checked
    setValues((prev) => {
      let prevProps = prev
      if (inputCheck) prevProps = INITIAL_VALUE
      const newValue = { ...prevProps, [inputName]: inputCheck }
      onChange && onChange(newValue)
      return newValue
    })
  }

  const profitFromPrice = useMemo(() => {
    const assetInfo = getAssetInfo(asset)
    const fillQuantity = (amount * leverage) / entryPrice
    const pnl = getPnL(entryPrice, values?.newTakeProfitPrice, side, fillQuantity, leverage, asset)

    if (pnl <= 0) return ''

    return currencyFormatter(pnl, {
      noCurrencySign: true,
      decimalDigits: assetInfo?.fiatDecimals,
    })
  }, [entryPrice, amount, leverage, values?.newTakeProfitPrice, side, asset])

  const profitFromValue = useMemo(() => {
    const assetInfo = getAssetInfo(asset)
    const takeProfitValue = values?.newTakeProfitValue || 0
    const quantity = (amount * leverage) / priceFeed.price
    let takeProfitPrice
    const feeRate = 0.23

    if (takeProfitValue===0) return ''

    if (side === 'buy') takeProfitPrice = entryPrice + takeProfitValue / (quantity * (1 - feeRate))
    else takeProfitPrice = entryPrice - takeProfitValue / (quantity * (1 - feeRate))

    return currencyFormatter(takeProfitPrice, {
      noCurrencySign: false,
      decimalDigits: assetInfo?.fiatDecimals,
    })
  }, [amount, priceFeed?.price, leverage, side, entryPrice, values?.newTakeProfitValue, asset])

  const pricePlaceholder = useMemo(() => {
    if (profitFromValue) return profitFromValue
    if (isFocused?.newTakeProfitPrice) return ``
    if (!takeProfitPrice) return `${side === 'buy' ? 'Min' : 'Max'} Price`
    const assetInfo = getAssetInfo(asset)
    return currencyFormatter(takeProfitPrice, {
      noCurrencySign: false,
      decimalDigits: assetInfo?.fiatDecimals,
    })
   }, [isFocused?.newTakeProfitPrice, asset, profitFromValue, side, takeProfitPrice])

  return <TakeProfitEditCSS>
    <div className='max'>
      <fieldset>
        <span>{`Close bid at ${side === 'buy' ? 'max' : 'min'} price`}</span>
        <input
          type='text'
          name='newTakeProfitPrice'      
          placeholder={pricePlaceholder}
          value={values?.newTakeProfitPrice||''}
          onChange={onInputChange}
          onClick={onInputClick}
          onFocus={onInputClick}
          onBlur={onInputBlur}
          disabled={values.cancelTakeProfitOrder}
        />
      </fieldset>
      <fieldset>
        <strong>Profit</strong>
        <div
          className={`input_wrapper profit ${isFocused?.newTakeProfitValue?'active':''}`}
        >
          <input
            type='text'
            name='newTakeProfitValue'      
            placeholder={profitFromPrice? `+${profitFromPrice}`: takeProfitValue || isFocused?.newTakeProfitValue?'':LONG_DASH}
            className='tpsl_result_data profit'
            value={values?.newTakeProfitValue||'' }
            onChange={onInputChange}
            onClick={onInputClick}
            onFocus={onInputClick}
            onBlur={onInputBlur}
            
            disabled={values.cancelTakeProfitOrder}

          />
        </div> 
      </fieldset> 
    </div>
    <div className='cancel_check'>
      <input
        type='checkbox'
        name='cancelTakeProfitOrder'
        className='max_switch'
        checked={values.cancelTakeProfitOrder}
        onChange={onCheckChange}
      />
      Cancel <strong>Take Profit</strong> trigger
    </div>
  </TakeProfitEditCSS>
}